/**
 * Normalizacao unica de telefone brasileiro para E.164 (IDENT-01).
 *
 * Copia fiel de src/shared/phone/brazilianPhone.ts do backend: mesma regra e
 * mesma suite de testes, porque os dois lados precisam concordar sobre qual
 * numero identifica o cliente. Se um mudar, o outro muda junto.
 *
 * Devolve null em vez de lancar porque o uso principal e comparacao e exibicao
 * de dados ja gravados, onde numero invalido e esperado e nao e erro.
 */

// DDDs em uso no Brasil. A lista evita aceitar numero estrangeiro de 11
// digitos como se fosse celular brasileiro.
const VALID_AREA_CODES = new Set([
  11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 24, 27, 28, 31, 32, 33, 34, 35, 37, 38, 41,
  42, 43, 44, 45, 46, 47, 48, 49, 51, 53, 54, 55, 61, 62, 63, 64, 65, 66, 67, 68, 69, 71,
  73, 74, 75, 77, 79, 81, 82, 83, 84, 85, 86, 87, 88, 89, 91, 92, 93, 94, 95, 96, 97, 98,
  99,
]);

/**
 * Forma canonica do numero nacional (DDD + assinante), ou null.
 *
 * Celular no Brasil ganhou um nono digito, mas o WhatsApp entrega o numero na
 * forma antiga, com 8 digitos. Sem tratar isso, o mesmo celular vira duas
 * pessoas diferentes: uma com o 9 que o cliente digitou e outra sem, que o
 * WhatsApp mandou. A saida canonica sempre tem o nono digito.
 *
 * O que separa celular de fixo no bloco de 8 digitos e o primeiro deles: fixo
 * comeca de 2 a 5, celular antigo de 6 a 9.
 */
function canonicalBrazilianNational(national: string): string | null {
  if (!/^\d{10,11}$/.test(national)) return null;
  if (!VALID_AREA_CODES.has(Number(national.slice(0, 2)))) return null;
  const areaCode = national.slice(0, 2);
  const subscriber = national.slice(2);

  if (subscriber.length === 9) {
    return subscriber.startsWith("9") ? national : null;
  }
  if (/^[2-5]/.test(subscriber)) return national;
  // Celular na forma antiga: recupera o nono digito.
  if (/^[6-9]/.test(subscriber)) return `${areaCode}9${subscriber}`;
  return null;
}

export function normalizeBrazilianE164(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  const digits = raw.replace(/\D/g, "");
  if (!digits) return null;

  // Numero estrangeiro so e aceito quando o autor foi explicito com o sinal
  // de mais. Sem isso nao ha como distinguir DDI de DDD.
  if (raw.startsWith("+") && !digits.startsWith("55")) {
    return /^\d{8,15}$/.test(digits) ? `+${digits}` : null;
  }

  const national =
    digits.startsWith("55") && digits.length >= 12 ? digits.slice(2) : digits;

  const canonical = canonicalBrazilianNational(national);
  return canonical ? `+55${canonical}` : null;
}

export function formatBrazilianPhoneDisplay(value: string | null | undefined): string {
  const e164 = normalizeBrazilianE164(value);
  if (!e164) return "";
  if (!e164.startsWith("+55")) return e164;
  const national = e164.slice(3);
  const areaCode = national.slice(0, 2);
  const subscriber = national.slice(2);
  const prefixLength = subscriber.length === 9 ? 5 : 4;
  return `(${areaCode}) ${subscriber.slice(0, prefixLength)}-${subscriber.slice(prefixLength)}`;
}
